<div class="flex h-[70vh] max-h-[70vh] flex-col gap-2">
  <div class="flex flex-wrap items-center gap-2">
    <div class="relative min-w-[180px] flex-1">
      <i
        [class]="ICONS.magnifyingGlass"
        class="text-text-tertiary absolute top-1/2 left-2 -translate-y-1/2 text-sm"></i>
      <input
        type="text"
        class="form-input w-full pl-8 text-sm"
        [value]="searchTerm()"
        (input)="onSearchInput($any($event.target).value)"
        [attr.aria-label]="'features.plan.jobs.logs.searchPlaceholder' | translate"
        [placeholder]="'features.plan.jobs.logs.searchPlaceholder' | translate" />
    </div>
    <span class="text-text-tertiary font-mono text-xs whitespace-nowrap">
      {{
        'features.plan.jobs.logs.matchCount'
          | translate: { shown: matchCount(), total: totalCount() }
      }}
    </span>

    <app-button
      type="cancel"
      [icon]="ICONS.textAlignLeft"
      [active]="wrap()"
      (clickEvent)="toggleWrap()"
      [tooltip]="'features.plan.jobs.logs.wrap' | translate" />
    <app-button
      type="cancel"
      [icon]="ICONS.copy"
      (clickEvent)="copy()"
      [tooltip]="'features.plan.jobs.logs.copy' | translate" />
    <app-button
      type="cancel"
      [icon]="ICONS.download"
      (clickEvent)="download()"
      [tooltip]="'features.plan.jobs.logs.download' | translate" />
    <app-button
      type="cancel"
      [icon]="ICONS.arrowsClockwise"
      (clickEvent)="refresh()"
      [tooltip]="'features.plan.jobs.logs.refresh' | translate" />
  </div>

  <div class="flex flex-wrap items-center gap-1">
    <i [class]="ICONS.funnel" class="text-text-tertiary mr-1 text-sm"></i>
    @for (sev of severities; track sev) {
      <button
        type="button"
        (click)="toggleSeverity(sev)"
        [attr.aria-pressed]="isSeverityVisible(sev)"
        [class]="
          isSeverityVisible(sev)
            ? severityBadgeClass(sev)
            : 'text-text-tertiary bg-transparent line-through opacity-60'
        "
        class="cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase transition-colors">
        {{ sev }}
      </button>
    }
  </div>

  <div
    class="border-default bg-page relative min-h-0 flex-1 overflow-hidden rounded border"
    role="log"
    aria-live="polite"
    [attr.aria-label]="'features.plan.jobs.logs.title' | translate">
    @if (initialLoading()) {
      <div class="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <svg
          width="20"
          height="20"
          viewBox="0 0 16 16"
          class="text-accent-main"
          style="animation: spin 0.9s linear infinite">
          <circle
            cx="8"
            cy="8"
            r="6"
            fill="none"
            stroke="currentColor"
            stroke-opacity="0.2"
            stroke-width="2" />
          <path
            d="M 8 2 a 6 6 0 0 1 6 6"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round" />
        </svg>
        <p class="text-text-secondary text-sm">
          {{
            (chaining()
              ? 'features.plan.jobs.logs.searching'
              : 'features.plan.jobs.logs.loading'
            ) | translate
          }}
        </p>
      </div>
    } @else if (errorState(); as err) {
      <div class="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <i [class]="ICONS.scroll" class="text-text-tertiary text-3xl"></i>
        <p class="text-text-secondary text-sm">
          {{ 'features.plan.jobs.logs.errors.' + err.kind | translate }}
        </p>
        @if (err.explanation) {
          <p class="text-text-tertiary text-xs">{{ err.explanation }}</p>
        }
        @if (err.canRetry) {
          <app-button
            type="secondary"
            [label]="'features.plan.jobs.logs.retry' | translate"
            (clickEvent)="retry()" />
        }
      </div>
    } @else if (isEmpty()) {
      <div class="flex h-full items-center justify-center p-6 text-center">
        <p class="text-text-secondary text-sm">
          {{ 'features.plan.jobs.logs.empty' | translate }}
        </p>
      </div>
    } @else {
      @if (virtualize()) {
        <cdk-virtual-scroll-viewport [itemSize]="rowHeight" class="log-scroll h-full">
          <ng-container *cdkVirtualFor="let entry of filteredEntries()">
            <ng-container
              [ngTemplateOutlet]="rowTpl"
              [ngTemplateOutletContext]="{ $implicit: entry }" />
          </ng-container>
        </cdk-virtual-scroll-viewport>
      } @else {
        <div class="log-scroll h-full overflow-auto">
          @for (entry of filteredEntries(); track $index) {
            <ng-container
              [ngTemplateOutlet]="rowTpl"
              [ngTemplateOutletContext]="{ $implicit: entry }" />
          }
        </div>
      }
    }
  </div>

  @if (hasMore() && !initialLoading()) {
    <div class="flex items-center justify-center gap-2 pt-1">
      @if (chaining() || loadingMore()) {
        <span class="text-text-tertiary text-xs">
          {{ 'features.plan.jobs.logs.searching' | translate }}
        </span>
      }
      <app-button
        type="secondary"
        [label]="'features.plan.jobs.logs.loadMore' | translate"
        [disabled]="loadingMore()"
        (clickEvent)="loadMore()" />
    </div>
  }
</div>

<ng-template #rowTpl let-entry>
  <div
    class="border-weak flex items-start gap-2 border-b border-l-2 px-2 py-1 font-mono text-xs"
    [class]="severityBorderClass(entry.severity)"
    [class.h-7]="virtualize()"
    [class.overflow-hidden]="virtualize()">
    <span class="text-text-tertiary shrink-0 tabular-nums" [title]="entry.timestamp">
      {{ formatTime(entry.timestamp) }}
    </span>
    <span
      class="shrink-0 rounded px-1.5 py-0.5 text-[10px] leading-none font-semibold uppercase"
      [class]="severityBadgeClass(entry.severity)">
      {{ entry.severity }}
    </span>
    <span
      class="text-text-primary min-w-0 flex-1"
      [class]="
        wrap() && !virtualize()
          ? 'break-words whitespace-pre-wrap'
          : 'overflow-x-auto whitespace-pre'
      ">
      {{ entry.message }}
    </span>
  </div>
</ng-template>
